import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { useBranch } from '../contexts/BranchContext'
import { useAuth } from '../contexts/AuthContext'
import { customerService, washService, subscriptionService } from '../services/firebase'
import { qrService } from '../services/qrService'
import { capacityService } from '../services/capacityService'
import { voucherService } from '@/services/voucherService'
import { Badge } from '@/components/ui/badge'
import {
  washPackages as staticWashPackages,
  defaultPackageId as staticDefaultPackageId,
  getTier,
  formatWashSelectionLabel,
  isTierPOA,
  formatTierPriceDisplay,
  resolveTierAmount,
} from '../config/washCatalog'
import { washCatalogService } from '@/services/washCatalogService'
import {
  Car,
  AlertCircle,
  Camera,
  Phone,
  QrCode,
  ImageIcon,
  CheckCircle2,
  MapPin,
  UserPlus,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'
import ScanMethodSelector from '@/components/scan/ScanMethodSelector'
import PlateScanner from '@/components/scan/PlateScanner'
import {
  PlateFoundPanel,
  PlateNotFoundPanel,
  PlateOcrFailedPanel,
  NewVehicleForCustomerPanel,
} from '@/components/scan/PlateLookupPanels'
import { vehicleLookupService } from '@/services/vehicleLookupService'
import { normalizeLicensePlate, platesEquivalent } from '@/utils/licensePlate'

function vehicleFromCustomer(c) {
  const v = c?.primaryVehicle || c?.vehicle || c?.carDetails || {}
  return {
    make: String(v.make || v.brand || '').trim(),
    model: String(v.model || '').trim(),
    color: String(v.color || v.colour || '').trim(),
    licensePlate: String(v.licensePlate || v.registration || v.reg || '').trim(),
  }
}

function emptyCarDetails() {
  return { make: '', model: '', color: '', licensePlate: '' }
}

function subscriptionCoversSelection(sub, pkgId, tierId) {
  if (!sub?.isActive) return false
  const selectedTierKey = `${pkgId}:${tierId}`
  const eligibleTiers = Array.isArray(sub?.eligibleTierIds) ? sub.eligibleTierIds : []
  const eligibleServices = Array.isArray(sub?.eligibleServiceIds) ? sub.eligibleServiceIds : []
  if (eligibleTiers.length > 0) return eligibleTiers.includes(selectedTierKey)
  if (eligibleServices.length > 0) return eligibleServices.includes(pkgId)
  return true
}

export default function ScanPage() {
  const { currentBranch, isViewingAllBranches, getEffectiveBranchId } = useBranch()
  const { currentUser } = useAuth()
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [showAddCustomer, setShowAddCustomer] = useState(false)
  const [savingCustomer, setSavingCustomer] = useState(false)
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    surname: '',
    phone: '',
    email: '',
  })
  const [capacityInfo, setCapacityInfo] = useState(null)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [scannerStarting, setScannerStarting] = useState(false)
  const [scanHint, setScanHint] = useState('')
  const [identifyMode, setIdentifyMode] = useState(null)
  const [plateScannerOpen, setPlateScannerOpen] = useState(false)
  const [plateUiStep, setPlateUiStep] = useState('camera')
  const [pendingPlate, setPendingPlate] = useState('')
  const [plateLookup, setPlateLookup] = useState(null)
  const [plateLookupLoading, setPlateLookupLoading] = useState(false)
  const [showNewVehiclePrompt, setShowNewVehiclePrompt] = useState(false)
  const [associatingVehicle, setAssociatingVehicle] = useState(false)
  const scannerRef = useRef(null)
  const fileInputRef = useRef(null)
  const handlingScanRef = useRef(false)

  const [washPackages, setWashPackages] = useState(staticWashPackages)
  const [packageId, setPackageId] = useState(staticDefaultPackageId)
  const pkg = washPackages.find((p) => p.id === packageId) || washPackages[0]
  const firstTier = pkg?.tiers[0]
  const [tierId, setTierId] = useState(firstTier?.id ?? '')

  useEffect(() => {
    const p = washPackages.find((x) => x.id === packageId) || washPackages[0]
    if (p?.tiers?.length) {
      const stillValid = p.tiers.some((t) => t.id === tierId)
      if (!stillValid) setTierId(p.tiers[0].id)
    }
  }, [packageId, tierId, washPackages])

  const tier = getTier(pkg, tierId)
  const tierIsPOA = isTierPOA(tier)
  const [poaAmount, setPoaAmount] = useState('')
  const amount = resolveTierAmount(tier, poaAmount)
  const selectionLabel = formatWashSelectionLabel(pkg, tier)

  const [washForm, setWashForm] = useState({
    carDetails: emptyCarDetails(),
  })

  const [activeSubscription, setActiveSubscription] = useState(null)
  const [applySubscription, setApplySubscription] = useState(false)
  const [applyOnAccount, setApplyOnAccount] = useState(false)
  const [matchingVouchers, setMatchingVouchers] = useState([])
  const [selectedVoucherId, setSelectedVoucherId] = useState('')

  const subscriptionEligible = useMemo(() => {
    if (!activeSubscription || !pkg?.id || !tier?.id) return false
    return subscriptionCoversSelection(activeSubscription, pkg.id, tier.id)
  }, [activeSubscription, pkg?.id, tier?.id])

  useEffect(() => {
    setApplySubscription(false)
    setApplyOnAccount(false)
    setSelectedVoucherId('')
    setPoaAmount('')
  }, [packageId, tierId, customer?.id])

  useEffect(() => {
    if (applySubscription) {
      setSelectedVoucherId('')
      setApplyOnAccount(false)
    }
  }, [applySubscription])

  useEffect(() => {
    if (selectedVoucherId) {
      setApplySubscription(false)
      setApplyOnAccount(false)
    }
  }, [selectedVoucherId])

  useEffect(() => {
    if (applyOnAccount) {
      setApplySubscription(false)
      setSelectedVoucherId('')
    }
  }, [applyOnAccount])

  useEffect(() => {
    ;(async () => {
      if (!customer?.id) {
        setActiveSubscription(null)
        return
      }
      try {
        const sub = await subscriptionService.getCustomerSubscription(customer.id)
        setActiveSubscription(sub?.isActive ? sub : null)
      } catch (e) {
        console.error(e)
        setActiveSubscription(null)
      }
    })()
  }, [customer?.id])

  useEffect(() => {
    ;(async () => {
      if (!customer?.id) {
        setMatchingVouchers([])
        return
      }
      try {
        const list = await voucherService.listCustomerVouchers(customer.id, 50)
        const active = (list || []).filter((v) => v.status === 'active')
        const matches = active.filter((v) => {
          if (v.rewardKind !== 'service') return false
          return (
            String(v.catalogServiceId || '') === String(pkg?.id || '') &&
            String(v.catalogTierId || '') === String(tier?.id || '')
          )
        })
        setMatchingVouchers(matches)
      } catch (e) {
        console.error(e)
        setMatchingVouchers([])
      }
    })()
  }, [customer?.id, pkg?.id, tier?.id])

  useEffect(() => {
    setWashForm({
      carDetails: customer ? vehicleFromCustomer(customer) : emptyCarDetails(),
    })
  }, [customer?.id, customer?.primaryVehicle])

  useEffect(() => {
    return () => {
      void (async () => {
        if (!scannerRef.current) return
        try {
          const s = scannerRef.current
          if (s.isScanning) await s.stop()
          await s.clear()
        } catch (e) {
          console.error(e)
        } finally {
          scannerRef.current = null
        }
      })()
    }
  }, [])

  useEffect(() => {
    if (currentBranch) loadCapacityInfo()
  }, [currentBranch])

  useEffect(() => {
    ;(async () => {
      try {
        await washCatalogService.ensureInitialized()
        const doc = await washCatalogService.get()
        if (doc?.packages?.length) {
          setWashPackages(doc.packages)
          if (!doc.packages.some((p) => p.id === packageId)) {
            setPackageId(doc.packages[0].id)
          }
        }
      } catch (e) {
        console.error(e)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function loadCapacityInfo() {
    try {
      const branchId = getEffectiveBranchId()
      if (!branchId || branchId === 'all-branches') return
      const capacity = await capacityService.canAcceptWash(branchId)
      setCapacityInfo(capacity)
    } catch (e) {
      console.error(e)
    }
  }

  async function closeScanner() {
    setScanHint('')
    setScannerStarting(false)
    if (scannerRef.current) {
      try {
        const s = scannerRef.current
        if (s.isScanning) await s.stop()
        await s.clear()
      } catch (e) {
        console.error(e)
      } finally {
        scannerRef.current = null
      }
    }
    setScannerOpen(false)
  }

  function resetPlateFlow() {
    setPlateScannerOpen(false)
    setPlateUiStep('camera')
    setPlateLookup(null)
    setPlateLookupLoading(false)
  }

  function resetIdentifyFlow() {
    setIdentifyMode(null)
    resetPlateFlow()
    setPendingPlate('')
    setShowNewVehiclePrompt(false)
  }

  async function loadCustomerFromQr(decodedText) {
    const qrData = qrService.validateQRData(decodedText)
    if (!qrData.isValid) {
      toast.error(qrData.error || 'Invalid QR code')
      return null
    }
    const customerData =
      (qrData.customerId ? await customerService.getCustomerById(qrData.customerId) : null) ||
      (qrData.customerCode ? await customerService.getCustomerByCode(qrData.customerCode) : null) ||
      (qrData.phone ? await customerService.getCustomerByPhone(qrData.phone) : null)
    if (!customerData) {
      toast.error('Customer not found for this QR code.')
      return null
    }
    if (qrData.sessionId && currentUser?.uid) {
      const branchId = getEffectiveBranchId()
      if (branchId && branchId !== 'all-branches') {
        try {
          await qrService.markSessionAsUsed(qrData.sessionId, branchId, currentUser.uid)
        } catch (e) {
          console.error(e)
        }
      }
    }
    return customerData
  }

  function applyCustomerLoaded(customerData, { fromPlate = false, plate = '' } = {}) {
    setShowAddCustomer(false)
    setCustomer(customerData)
    toast.success(`Customer loaded: ${customerData.name}`)

    const detected = normalizeLicensePlate(plate || pendingPlate)
    if (detected) {
      const profilePlate = normalizeLicensePlate(vehicleFromCustomer(customerData).licensePlate)
      if (!profilePlate || !platesEquivalent(profilePlate, detected)) {
        setPendingPlate(detected)
        setShowNewVehiclePrompt(true)
        setWashForm((prev) => ({
          ...prev,
          carDetails: {
            ...prev.carDetails,
            licensePlate: detected,
            ...(fromPlate && plateLookup?.vehicle
              ? {
                  make: plateLookup.vehicle.make || prev.carDetails.make,
                  model: plateLookup.vehicle.model || prev.carDetails.model,
                  color: plateLookup.vehicle.color || prev.carDetails.color,
                }
              : {}),
          },
        }))
      } else {
        setPendingPlate('')
        setShowNewVehiclePrompt(false)
      }
    }

    resetPlateFlow()
    if (!fromPlate) setIdentifyMode('qr')
  }

  async function lookupPlateRegistration(plate) {
    const norm = normalizeLicensePlate(plate)
    if (norm.length < 4) {
      toast.error('Enter a valid registration')
      return
    }
    setPlateLookupLoading(true)
    setPendingPlate(norm)
    try {
      const branchId = getEffectiveBranchId()
      const result = await vehicleLookupService.lookupByPlate(norm, branchId)
      if (result.found && result.customer?.id) {
        try {
          const sub = await subscriptionService.getCustomerSubscription(result.customer.id)
          result.subscriptionActive = Boolean(sub?.isActive)
        } catch (_) {
          result.subscriptionActive = false
        }
      }
      setPlateLookup(result)
      if (result.found) {
        setPlateUiStep('found')
        setPlateScannerOpen(false)
      } else {
        setPlateUiStep('not_found')
        setPlateScannerOpen(false)
      }
    } catch (e) {
      console.error(e)
      toast.error('Plate lookup failed')
    } finally {
      setPlateLookupLoading(false)
    }
  }

  async function continueFromPlateFound() {
    if (!plateLookup?.found || !plateLookup.customer) return
    setPlateLookupLoading(true)
    try {
      let sub = null
      try {
        sub = await subscriptionService.getCustomerSubscription(plateLookup.customer.id)
      } catch (_) {
        /* ignore */
      }
      applyCustomerLoaded(plateLookup.customer, { fromPlate: true, plate: plateLookup.plate })
      const vehicle = plateLookup.vehicle || {}
      setWashForm({
        carDetails: {
          make: String(vehicle.make || '').trim(),
          model: String(vehicle.model || '').trim(),
          color: String(vehicle.color || '').trim(),
          licensePlate: plateLookup.plate,
        },
      })
      if (sub?.isActive) setActiveSubscription(sub)
      setIdentifyMode('plate')
    } finally {
      setPlateLookupLoading(false)
    }
  }

  function switchToQrFromPlate() {
    resetPlateFlow()
    setIdentifyMode('qr')
    void startLiveScanner()
  }

  async function addDetectedVehicleToCustomer() {
    if (!customer?.id || !pendingPlate) return
    setAssociatingVehicle(true)
    try {
      const nextVehicle = {
        ...vehicleFromCustomer(customer),
        licensePlate: pendingPlate,
        make: washForm.carDetails.make || vehicleFromCustomer(customer).make,
        model: washForm.carDetails.model || vehicleFromCustomer(customer).model,
        color: washForm.carDetails.color || vehicleFromCustomer(customer).color,
      }
      await customerService.updatePrimaryVehicle(customer.id, nextVehicle)
      setCustomer((prev) => ({
        ...prev,
        primaryVehicle: nextVehicle,
        licensePlateNorm: normalizeLicensePlate(pendingPlate),
      }))
      setWashForm((prev) => ({
        ...prev,
        carDetails: { ...prev.carDetails, licensePlate: pendingPlate },
      }))
      setShowNewVehiclePrompt(false)
      setPendingPlate('')
      toast.success('Vehicle added to customer profile')
    } catch (e) {
      console.error(e)
      toast.error('Could not save vehicle to profile')
    } finally {
      setAssociatingVehicle(false)
    }
  }

  function continueWithoutAddingVehicle() {
    setShowNewVehiclePrompt(false)
    if (pendingPlate) {
      setWashForm((prev) => ({
        ...prev,
        carDetails: { ...prev.carDetails, licensePlate: pendingPlate },
      }))
    }
    setPendingPlate('')
  }

  const onScanSuccess = useCallback(
    async (decodedText) => {
      if (handlingScanRef.current) return
      handlingScanRef.current = true

      // Stop camera before navigating UI (avoids html5-qrcode race on success)
      try {
        if (scannerRef.current?.isScanning) {
          await scannerRef.current.stop()
        }
      } catch (e) {
        console.warn('Scanner stop:', e)
      }
      try {
        await scannerRef.current?.clear()
      } catch (_) {
        /* ignore */
      }
      scannerRef.current = null
      setScannerOpen(false)
      setScannerStarting(false)
      setScanHint('')

      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        try {
          navigator.vibrate(80)
        } catch (_) {
          /* ignore */
        }
      }

      setLoading(true)
      try {
        const customerData = await loadCustomerFromQr(decodedText)
        if (!customerData) return
        applyCustomerLoaded(customerData)
      } catch (e) {
        console.error(e)
        toast.error('Failed to process QR code')
      } finally {
        setLoading(false)
        handlingScanRef.current = false
      }
    },
    [currentBranch?.id, currentUser?.uid, getEffectiveBranchId]
  )

  async function startLiveScanner() {
    if (scannerOpen || scannerStarting) return
    if (isViewingAllBranches()) {
      toast.error('Select a specific branch first, then scan.')
      return
    }
    setScannerOpen(true)
    setScannerStarting(true)
    setScanHint('Starting camera… Allow camera access if asked.')

    // Wait for the preview element to mount
    await new Promise((r) => setTimeout(r, 120))

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Camera not supported on this browser. Use lookup or upload a QR photo.')
        await closeScanner()
        return
      }

      // Clear any leftover instance
      if (scannerRef.current) {
        try {
          if (scannerRef.current.isScanning) await scannerRef.current.stop()
          await scannerRef.current.clear()
        } catch (_) {
          /* ignore */
        }
        scannerRef.current = null
      }

      const element = document.getElementById('qr-reader')
      if (!element) {
        toast.error('Scanner view failed to load. Try again.')
        await closeScanner()
        return
      }

      const scanner = new Html5Qrcode('qr-reader', { verbose: false })
      scannerRef.current = scanner

      const config = {
        fps: 12,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const edge = Math.min(viewfinderWidth, viewfinderHeight)
          const size = Math.max(180, Math.floor(edge * 0.72))
          return { width: size, height: size }
        },
        aspectRatio: 1,
        disableFlip: false,
      }

      const onFail = () => {
        // Continuous scan — ignore frame-level decode misses
      }

      try {
        await scanner.start({ facingMode: 'environment' }, config, onScanSuccess, onFail)
      } catch (rearErr) {
        console.warn('Rear camera failed, trying default:', rearErr)
        await scanner.start({ facingMode: 'user' }, config, onScanSuccess, onFail)
      }

      setScannerStarting(false)
      setScanHint('Point at the customer’s QR — hold steady until it beeps.')
    } catch (err) {
      console.error(err)
      const msg = String(err?.message || err || '')
      if (/NotAllowed|Permission|denied/i.test(msg)) {
        toast.error('Camera permission blocked. Allow camera for this site, or use phone lookup.')
      } else if (/NotFound|DevicesNotFound/i.test(msg)) {
        toast.error('No camera found. Use phone lookup or upload a QR screenshot.')
      } else {
        toast.error('Could not start camera. Try again or look up by phone.')
      }
      await closeScanner()
    }
  }

  async function scanFromImageFile(file) {
    if (!file) return
    setLoading(true)
    try {
      // Use a throwaway scanner instance for file decode (no camera)
      const tempId = 'qr-file-reader'
      let el = document.getElementById(tempId)
      if (!el) {
        el = document.createElement('div')
        el.id = tempId
        el.className = 'hidden'
        document.body.appendChild(el)
      }
      const fileScanner = new Html5Qrcode(tempId, { verbose: false })
      const decodedText = await fileScanner.scanFile(file, true)
      try {
        await fileScanner.clear()
      } catch (_) {
        /* ignore */
      }
      await onScanSuccess(decodedText)
    } catch (e) {
      console.error(e)
      toast.error('Could not read a QR in that image. Ask the customer to open My QR, or look up by phone.')
    } finally {
      setLoading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  function openAddCustomerForm() {
    const q = String(phoneNumber || '').trim()
    const digits = q.replace(/\D/g, '')
    if (digits.length >= 8) {
      setNewCustomer({ firstName: '', surname: '', phone: q, email: '' })
    } else if (q.includes('@')) {
      setNewCustomer({ firstName: '', surname: '', phone: '', email: q })
    } else {
      const parts = q.split(/\s+/).filter(Boolean)
      const looksLikeCode = /^TS[-\s_]*[A-Z0-9]{3,}$/i.test(q)
      setNewCustomer({
        firstName: looksLikeCode ? '' : parts[0] || '',
        surname: looksLikeCode ? '' : parts.slice(1).join(' '),
        phone: '',
        email: '',
      })
    }
    setShowAddCustomer(true)
  }

  async function createScanCustomer(e) {
    e?.preventDefault?.()
    const firstName = String(newCustomer.firstName || '').trim()
    const surname = String(newCustomer.surname || '').trim()
    const phoneRaw = String(newCustomer.phone || '').trim()
    const email = String(newCustomer.email || '').trim().toLowerCase()
    const name = [firstName, surname].filter(Boolean).join(' ')
    const phone = customerService.normalizePhone(phoneRaw) || phoneRaw.replace(/\s+/g, '')

    if (!firstName) {
      toast.error('Name is required')
      return
    }
    if (!phone || phone.replace(/\D/g, '').length < 9) {
      toast.error('A valid mobile number is required')
      return
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email, or leave it blank')
      return
    }

    setSavingCustomer(true)
    try {
      const existing = await customerService.getCustomerByPhone(phone)
      if (existing?.id) {
        setCustomer(existing)
        setShowAddCustomer(false)
        setPhoneNumber('')
        toast.success(`Existing customer loaded: ${existing.name || name}`)
        return
      }

      const branchId = getEffectiveBranchId()
      const payload = {
        name,
        firstName,
        surname,
        phone,
        email: email || '',
        branchId: branchId && branchId !== 'all-branches' ? branchId : null,
        loyaltyPoints: 0,
        voucherBalance: 0,
        voucherSpendProgress: 0,
        totalWashes: 0,
        isSystemProfile: false,
        createdFrom: 'scan',
      }
      const id = await customerService.createCustomer(payload)
      setCustomer({ ...payload, id })
      setShowAddCustomer(false)
      setPhoneNumber('')
      setNewCustomer({ firstName: '', surname: '', phone: '', email: '' })
      toast.success('Customer created')
    } catch (err) {
      console.error(err)
      toast.error('Could not create customer')
    } finally {
      setSavingCustomer(false)
    }
  }

  async function findCustomer() {
    const q = String(phoneNumber || '').trim()
    if (!q) {
      toast.error('Enter a phone, name, or customer code')
      return
    }
    setLoading(true)
    try {
      const looksLikeCode = /^TS[-\s_]*[A-Z0-9]{3,}$/i.test(q)
      const hasLetters = /[a-zA-Z]/.test(q)
      const customerData =
        (looksLikeCode ? await customerService.getCustomerByCode(q.replace(/\s+/g, '')) : null) ||
        (!hasLetters ? await customerService.getCustomerByPhone(q) : null) ||
        (hasLetters ? await customerService.searchCustomerByNamePrefix(q) : null)
      if (!customerData) {
        toast.error('No customer found. Add them below, or try a customer code / different phone format.')
        openAddCustomerForm()
        return
      }
      setShowAddCustomer(false)
      setCustomer(customerData)
      toast.success(`Customer: ${customerData.name}`)
    } catch (e) {
      console.error(e)
      toast.error('Lookup failed')
    } finally {
      setLoading(false)
    }
  }

  function handleCarChange(e) {
    const { name, value } = e.target
    setWashForm((prev) => ({
      ...prev,
      carDetails: { ...prev.carDetails, [name]: value },
    }))
  }

  async function createWash() {
    if (!customer) {
      toast.error('Find the customer first')
      return
    }
    if (!pkg || !tier) {
      toast.error('Select a wash package and vehicle class')
      return
    }
    if (!washForm.carDetails.make?.trim() || !washForm.carDetails.color?.trim()) {
      toast.error('Enter at least make and colour')
      return
    }
    const branchId = getEffectiveBranchId()
    if (!branchId || branchId === 'all-branches' || isViewingAllBranches()) {
      toast.error('Select a specific branch before check-in')
      return
    }
    if (tierIsPOA && amount <= 0) {
      toast.error('Enter the price on arrival for this service')
      return
    }

    setLoading(true)
    try {
      const capacityValidation = await capacityService.validateWashCreation(
        branchId
      )
      if (!capacityValidation.isValid) {
        toast.error(capacityValidation.errors[0])
        return
      }
      capacityValidation.warnings.forEach((w) => toast(w, { icon: '!' }))

      if (applySubscription) {
        if (!activeSubscription || !subscriptionEligible) {
          toast.error('Subscription cannot be applied for this service and vehicle type.')
          return
        }
        const ok = window.confirm(
          `Apply subscription for "${selectionLabel}"?\n\nCustomer: ${customer.name}`
        )
        if (!ok) return
      }

      if (selectedVoucherId) {
        const v = (matchingVouchers || []).find((x) => x.id === selectedVoucherId)
        if (!v) {
          toast.error('Voucher not found for this service')
          return
        }
        const ok = window.confirm(
          `Redeem 1 voucher for "${selectionLabel}"?\n\nCustomer: ${customer.name}`
        )
        if (!ok) return
      }

      if (applyOnAccount) {
        if (!customer.allowOnAccount) {
          toast.error('This customer is not enabled for on-account billing.')
          return
        }
        const ok = window.confirm(
          `Charge "${selectionLabel}" to customer account (R${amount.toFixed(2)})?\n\nCustomer: ${customer.name}\n\nBalance will be settled per your agreement (e.g. month-end).`
        )
        if (!ok) return
      }

      const useSubscription = applySubscription && subscriptionEligible
      const useVoucher = Boolean(selectedVoucherId)
      const useOnAccount = applyOnAccount && customer.allowOnAccount
      const carDetailsForWash = {
        ...washForm.carDetails,
        licensePlateNorm: normalizeLicensePlate(washForm.carDetails.licensePlate),
      }
      const washData = {
        customerId: customer.id,
        customerName: customer.name,
        customerPhone: customer.phone,
        branchId,
        staffId: currentUser.uid,
        serviceType: selectionLabel,
        amount,
        catalogServiceId: pkg.id,
        catalogTierId: tier.id,
        vehicleClassLabel: tier.label,
        priceOnArrival: tierIsPOA,
        carDetails: carDetailsForWash,
        paymentStatus: useSubscription || useVoucher ? 'paid' : useOnAccount ? 'on_account' : 'unpaid',
        paymentMethod: useSubscription
          ? 'subscription'
          : useVoucher
            ? 'voucher'
            : useOnAccount
              ? 'account'
              : 'cash',
        status: 'waiting',
      }

      const washId = await washService.createWash(washData)

      if (useOnAccount) {
        try {
          await customerService.adjustAccountBalance(customer.id, amount)
        } catch (e) {
          console.error(e)
          toast.error('Wash added, but account balance was not updated.')
        }
      }

      if (useVoucher && selectedVoucherId) {
        try {
          await voucherService.redeemVoucher({
            voucherId: selectedVoucherId,
            redeemedByUid: currentUser?.uid,
            redeemedTransactionId: null,
            redeemedWashId: washId,
          })
        } catch (e) {
          console.error(e)
          toast.error('Wash created, but voucher redemption failed. Please redeem manually.')
        }
      }

      await customerService.appendWashSelection(customer.id, {
        washId,
        branchId,
        catalogServiceId: pkg.id,
        catalogTierId: tier.id,
        serviceName: pkg.name,
        tierLabel: tier.label,
        amount,
        label: selectionLabel,
        carDetails: carDetailsForWash,
      })

      setCustomer(null)
      setPhoneNumber('')
      resetIdentifyFlow()
      setWashForm({
        carDetails: emptyCarDetails(),
      })
      setActiveSubscription(null)
      setApplySubscription(false)
      setApplyOnAccount(false)
      setMatchingVouchers([])
      setSelectedVoucherId('')
      await loadCapacityInfo()
      toast.success(
        useOnAccount
          ? 'Wash added on account. Customer can collect when ready; settle balance per agreement.'
          : useSubscription || useVoucher
            ? 'Wash added to queue (covered). Cashier does not need to charge for this wash.'
            : 'Wash added to queue (unpaid). Cashier will take payment on POS.'
      )
    } catch (e) {
      console.error(e)
      toast.error('Could not add wash')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-background pb-10">
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-8">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Scan QR / check-in
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Identify the customer by QR or licence plate, confirm the wash, then add them to the queue.
          </p>
        </div>

        <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <ol className="grid gap-2 text-sm sm:grid-cols-4">
            {[
              { n: '1', t: 'Identify customer', d: 'Scan QR, licence plate, or look up by phone' },
              { n: '2', t: 'Scan or find', d: 'QR auto-scans; plate uses Scan button' },
              { n: '3', t: 'Confirm wash', d: 'Package, vehicle class & car details' },
              { n: '4', t: 'Queue', d: 'Add to wash queue — cashier takes payment' },
            ].map((s) => (
              <li key={s.n} className="flex gap-2">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                  {s.n}
                </span>
                <span>
                  <span className="font-medium text-foreground">{s.t}</span>
                  <span className="block text-xs text-muted-foreground">{s.d}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>

        {isViewingAllBranches() && (
          <div
            className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100"
            role="status"
          >
            <MapPin className="size-4 shrink-0 mt-0.5" />
            <span>
              Select a <span className="font-semibold">specific branch</span> in the header before scanning.
              Check-in cannot run on “All Branches”.
            </span>
          </div>
        )}

        {capacityInfo && Number(capacityInfo.currentQueueSize) > 0 && (
          <div
            className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
            role="status"
          >
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            <span>
              {capacityInfo.currentQueueSize} vehicle
              {capacityInfo.currentQueueSize === 1 ? '' : 's'} currently at this wash
              (in progress or ready).
            </span>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <QrCode className="size-4" />
                1. Identify customer
              </CardTitle>
              <CardDescription>
                Scan a customer QR or licence plate, or look up by phone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {showNewVehiclePrompt && customer && pendingPlate && (
                <NewVehicleForCustomerPanel
                  plate={pendingPlate}
                  onAddVehicle={() => void addDetectedVehicleToCustomer()}
                  onContinueWithout={continueWithoutAddingVehicle}
                  loading={associatingVehicle}
                />
              )}

              {plateUiStep === 'found' && plateLookup?.found && !customer && (
                <PlateFoundPanel
                  plate={plateLookup.plate}
                  vehicleLabel={plateLookup.vehicleLabel}
                  customerName={plateLookup.customer?.name}
                  totalWashes={plateLookup.customer?.totalWashes}
                  subscriptionActive={Boolean(plateLookup.subscriptionActive)}
                  loyaltyPoints={plateLookup.customer?.loyaltyPoints}
                  onContinue={() => void continueFromPlateFound()}
                  loading={plateLookupLoading}
                />
              )}

              {plateUiStep === 'not_found' && pendingPlate && !customer && (
                <PlateNotFoundPanel
                  plate={pendingPlate}
                  onScanCustomerQr={switchToQrFromPlate}
                  onTryAgain={() => {
                    setPlateUiStep('camera')
                    setPlateScannerOpen(true)
                  }}
                  onManualEntry={() => {
                    setPlateUiStep('camera')
                    setPlateScannerOpen(true)
                  }}
                />
              )}

              {plateUiStep === 'ocr_failed' && !customer && (
                <PlateOcrFailedPanel
                  onTryAgain={() => {
                    setPlateUiStep('camera')
                    setPlateScannerOpen(true)
                  }}
                  onScanCustomerQr={switchToQrFromPlate}
                  onManualEntry={() => {
                    setPlateUiStep('camera')
                    setPlateScannerOpen(true)
                  }}
                />
              )}

              {plateScannerOpen && !customer && plateUiStep === 'camera' && (
                <PlateScanner
                  open={plateScannerOpen}
                  onClose={() => {
                    resetPlateFlow()
                    setIdentifyMode(null)
                  }}
                  onPlateDetected={(plate) => void lookupPlateRegistration(plate)}
                  onOcrFailed={() => {
                    setPlateScannerOpen(false)
                    setPlateUiStep('ocr_failed')
                  }}
                  onScanCustomerQr={switchToQrFromPlate}
                  onManualEntry={(plate) => void lookupPlateRegistration(plate)}
                />
              )}

              {!plateScannerOpen &&
              plateUiStep === 'camera' &&
              !scannerOpen &&
              !customer &&
              identifyMode === null &&
              !plateLookup?.found ? (
                <>
                  <ScanMethodSelector
                    disabled={loading || isViewingAllBranches()}
                    onSelectQr={() => setIdentifyMode('qr')}
                    onSelectPlate={() => {
                      if (isViewingAllBranches()) {
                        toast.error('Select a specific branch first.')
                        return
                      }
                      setIdentifyMode('plate')
                      setPlateScannerOpen(true)
                      setPlateUiStep('camera')
                    }}
                  />
                  <Separator />
                </>
              ) : null}

              {!scannerOpen &&
                !plateScannerOpen &&
                plateUiStep === 'camera' &&
                !customer && (
                <div className="space-y-3">
                  {!customer && identifyMode === 'qr' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="px-0 h-auto text-muted-foreground"
                      onClick={resetIdentifyFlow}
                    >
                      ← Choose another method
                    </Button>
                  )}

                  {!customer && identifyMode === 'qr' && (
                    <>
                      <Button
                        type="button"
                        className="h-12 w-full text-base"
                        onClick={() => void startLiveScanner()}
                        disabled={loading || scannerStarting || isViewingAllBranches()}
                      >
                        <Camera className="size-5" />
                        Scan customer QR
                      </Button>
                      <p className="text-center text-xs text-muted-foreground">
                        Point the camera at the QR on the customer&apos;s phone — it scans automatically.
                      </p>
                      <Separator />
                    </>
                  )}

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="phone">Or look up / add customer</Label>
                      {!customer && (
                        <button
                          type="button"
                          onClick={openAddCustomerForm}
                          className="text-xs font-semibold text-primary hover:underline"
                        >
                          + Add customer
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="phone"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Phone, name, or TS-XXXXXX"
                        className="font-mono text-sm"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void findCustomer()
                        }}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={findCustomer}
                        disabled={loading}
                        className="shrink-0"
                      >
                        <Phone className="size-4" />
                        Find
                      </Button>
                    </div>
                  </div>

                  {showAddCustomer && !customer && (
                    <form
                      onSubmit={createScanCustomer}
                      className="space-y-2 rounded-xl border border-border bg-muted/20 p-3"
                    >
                      <p className="text-xs font-semibold text-foreground">New customer</p>
                      <Input
                        required
                        type="text"
                        value={newCustomer.firstName}
                        onChange={(e) => setNewCustomer((p) => ({ ...p, firstName: e.target.value }))}
                        placeholder="Name *"
                      />
                      <Input
                        type="text"
                        value={newCustomer.surname}
                        onChange={(e) => setNewCustomer((p) => ({ ...p, surname: e.target.value }))}
                        placeholder="Surname"
                      />
                      <Input
                        required
                        type="tel"
                        value={newCustomer.phone}
                        onChange={(e) => setNewCustomer((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="Mobile number *"
                      />
                      <Input
                        type="email"
                        value={newCustomer.email}
                        onChange={(e) => setNewCustomer((p) => ({ ...p, email: e.target.value }))}
                        placeholder="Email (optional)"
                      />
                      <div className="flex gap-2 pt-1">
                        <Button type="submit" disabled={savingCustomer} className="flex-1">
                          <UserPlus className="size-4" />
                          {savingCustomer ? 'Saving…' : 'Save customer'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setShowAddCustomer(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  )}

                  {!customer && identifyMode === 'qr' && (
                    <div className="pt-1">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => void scanFromImageFile(e.target.files?.[0])}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground"
                        disabled={loading}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <ImageIcon className="size-4" />
                        Camera broken? Upload a QR screenshot
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {scannerOpen && (
                <div className="space-y-3">
                  <div className="relative overflow-hidden rounded-xl border bg-black">
                    <div
                      id="qr-reader"
                      className="qr-live-reader min-h-[280px] w-full [&_video]:max-h-[360px] [&_video]:w-full [&_video]:object-cover"
                    />
                    <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/70 to-transparent px-3 py-2.5">
                      <p className="text-center text-sm font-medium text-white">
                        {scanHint || 'Hold steady on the customer’s QR'}
                      </p>
                    </div>
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="size-[58%] max-w-[260px] rounded-2xl border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
                    </div>
                  </div>
                  {scannerStarting && (
                    <p className="text-center text-xs text-muted-foreground">Requesting camera…</p>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full"
                    onClick={() => void closeScanner()}
                  >
                    Cancel scan
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Tip: turn up the customer’s screen brightness and fill the frame with the QR.
                  </p>
                </div>
              )}

              {customer && (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 shrink-0 mt-0.5 text-emerald-600" />
                    <div>
                      <p className="font-medium text-foreground">{customer.name}</p>
                      <p className="text-muted-foreground">{customer.phone}</p>
                      {customer.customerCode && (
                        <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                          {customer.customerCode}
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-2 h-7 px-0"
                        onClick={() => {
                          setCustomer(null)
                          resetIdentifyFlow()
                        }}
                      >
                        Clear & scan another
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Wash package & vehicle</CardTitle>
              <CardDescription>
                Confirm with the driver before queueing. Totals follow your published list.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pkg">Service</Label>
                <select
                  id="pkg"
                  className={cn(
                    'flex h-9 w-full rounded-lg border border-input bg-background px-3 py-1 text-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                  value={packageId}
                  onChange={(e) => setPackageId(e.target.value)}
                >
                  {washPackages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {pkg?.description && (
                  <p className="text-xs text-muted-foreground">{pkg.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tier">Vehicle class</Label>
                <select
                  id="tier"
                  className={cn(
                    'flex h-9 w-full rounded-lg border border-input bg-background px-3 py-1 text-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                  value={tierId}
                  onChange={(e) => setTierId(e.target.value)}
                >
                  {(pkg?.tiers || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label} - {formatTierPriceDisplay(t)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-lg border bg-card px-4 py-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Selected
                </p>
                <p className="font-medium text-foreground">{selectionLabel}</p>
                {tierIsPOA ? (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm font-semibold text-amber-700">POA — price on arrival</p>
                    <div className="space-y-1">
                      <Label htmlFor="poaAmount" className="text-xs">
                        Enter amount (ZAR)
                      </Label>
                      <Input
                        id="poaAmount"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={poaAmount}
                        onChange={(e) => setPoaAmount(e.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="text-lg font-semibold tabular-nums mt-1">
                    {formatTierPriceDisplay(tier)}
                  </p>
                )}
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Vehicle</p>
                {customer &&
                (vehicleFromCustomer(customer).make ||
                  vehicleFromCustomer(customer).model ||
                  vehicleFromCustomer(customer).licensePlate) ? (
                  <p className="text-xs text-muted-foreground">
                    Pre-filled from this customer’s profile. Change if they brought a different car.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No vehicle on file — enter details for this visit.
                  </p>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="make">Make</Label>
                  <Input
                    id="make"
                    name="make"
                    value={washForm.carDetails.make}
                    onChange={handleCarChange}
                    placeholder="Toyota"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    name="model"
                    value={washForm.carDetails.model}
                    onChange={handleCarChange}
                    placeholder="Corolla"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="color">Colour</Label>
                  <Input
                    id="color"
                    name="color"
                    value={washForm.carDetails.color}
                    onChange={handleCarChange}
                    placeholder="White"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plate">Registration</Label>
                  <Input
                    id="plate"
                    name="licensePlate"
                    value={washForm.carDetails.licensePlate}
                    onChange={handleCarChange}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Payment at store
                </p>
                <p className="text-sm text-foreground">
                  Default is <span className="font-semibold">unpaid</span> (cashier on POS). Or use subscription, voucher, or on-account below if applicable.
                </p>
              </div>

              <div className="rounded-lg border px-4 py-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-base">Subscription</Label>
                  {activeSubscription ? (
                    <Badge variant="default">Active</Badge>
                  ) : (
                    <Badge variant="secondary">None</Badge>
                  )}
                </div>
                {!customer ? (
                  <p className="text-sm text-muted-foreground">Find a customer to check subscription.</p>
                ) : !activeSubscription ? (
                  <p className="text-sm text-muted-foreground">No active subscription on this account.</p>
                ) : subscriptionEligible ? (
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={applySubscription}
                      onChange={(e) => setApplySubscription(e.target.checked)}
                    />
                    <span>
                      Apply subscription for <span className="font-medium">{selectionLabel}</span>
                      <span className="block text-xs text-muted-foreground mt-1">
                        Wash is marked paid. No cashier charge for this service.
                      </span>
                    </span>
                  </label>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Active subscription does not cover the selected service and vehicle type. Customer pays at the cashier.
                  </p>
                )}
              </div>

              <div className="rounded-lg border px-4 py-3 space-y-2">
                <Label>Voucher (optional)</Label>
                <select
                  className={cn(
                    'flex h-9 w-full rounded-lg border border-input bg-background px-3 py-1 text-sm'
                  )}
                  value={selectedVoucherId}
                  onChange={(e) => setSelectedVoucherId(e.target.value)}
                  disabled={applySubscription}
                >
                  <option value="">
                    {matchingVouchers.length > 0
                      ? `Select 1 of ${matchingVouchers.length} matching voucher(s)...`
                      : 'No matching vouchers for this service'}
                  </option>
                  {matchingVouchers.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.rewardLabel || 'Voucher'} · {v.id.slice(0, 6).toUpperCase()}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Free service vouchers only apply to the selected package and vehicle type.
                </p>
              </div>

              <div className="rounded-lg border px-4 py-3 space-y-2">
                <Label className="text-base">On account</Label>
                {!customer ? (
                  <p className="text-sm text-muted-foreground">Find a customer first.</p>
                ) : !customer.allowOnAccount ? (
                  <p className="text-sm text-muted-foreground">
                    This customer is not set up for on-account billing. Enable under Customers.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Outstanding balance: R{Number(customer.accountBalance || 0).toFixed(2)}
                    </p>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={applyOnAccount}
                        onChange={(e) => setApplyOnAccount(e.target.checked)}
                        disabled={applySubscription || Boolean(selectedVoucherId)}
                      />
                      <span>
                        Charge to account for <span className="font-medium">{selectionLabel}</span>
                        <span className="block text-xs text-muted-foreground mt-1">
                          Vehicle can be collected when ready. Settle monthly or per agreement.
                        </span>
                      </span>
                    </label>
                  </>
                )}
              </div>

              <Button
                type="button"
                className="w-full"
                disabled={loading || !customer}
                onClick={createWash}
              >
                <Car className="size-4" />
                Add to wash queue
              </Button>
              {!customer && (
                <p className="text-xs text-center text-muted-foreground">
                  Scan, look up, or add a customer before queueing.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Intentionally no dev-only guidance shown to staff/users here */}
      </div>
    </div>
  )
}
